import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiPath } from '../../lib/http';

const SLOTS_TIMEOUT_MS = 15000;

const buildDateOptions = (days = 21) => {
  const items = [];
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  for (let i = 0; i < days; i += 1) {
    const next = new Date(base);
    next.setDate(base.getDate() + i);
    items.push({
      value: next.toISOString().slice(0, 10),
      weekday: next.toLocaleDateString([], { weekday: 'short' }),
      label: next.toLocaleDateString([], { month: 'short', day: 'numeric' }),
    });
  }
  return items;
};

export const CrmSlotPicker = ({
  token,
  selectedStart = '',
  selectedEnd = '',
  onSelect,
  testIdPrefix = 'crm-booking',
}) => {
  const dateOptions = useMemo(() => buildDateOptions(21), []);
  const [selectedDate, setSelectedDate] = useState(selectedStart?.slice(0, 10) || dateOptions[0]?.value || '');
  const [slots, setSlots] = useState([]);
  const [slotDuration, setSlotDuration] = useState(30);
  const [timezone, setTimezone] = useState('UTC');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedStart?.slice(0, 10)) {
      setSelectedDate(selectedStart.slice(0, 10));
    }
  }, [selectedStart]);

  useEffect(() => {
    if (!selectedDate || !token) {
      setSlots([]);
      setError(token ? '' : 'Sign in to load available slots.');
      return undefined;
    }

    let cancelled = false;
    let timedOut = false;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, SLOTS_TIMEOUT_MS);

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          apiPath(`/crm/appointments/slots?date=${encodeURIComponent(selectedDate)}`),
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        if (cancelled) return;
        setSlots(Array.isArray(data.slots) ? data.slots : []);
        setSlotDuration(Number(data.slot_duration_minutes) || 30);
        setTimezone(data.timezone || 'UTC');
      } catch (err) {
        if (cancelled) return;
        if (err.name === 'AbortError' && !timedOut) return;
        setSlots([]);
        setError(
          err.name === 'AbortError'
            ? 'Available slots took too long to load. Check your connection and retry.'
            : (err.message || 'Failed to load slots'),
        );
      } finally {
        window.clearTimeout(timer);
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [selectedDate, token]);

  const selectedKey = selectedStart && selectedEnd ? `${selectedStart}|${selectedEnd}` : '';

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-slot-picker`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <label className="block text-zinc-400 mb-1">Pick a date</label>
          <p className="text-[10px] text-zinc-500">{slotDuration}-minute slots · {timezone}</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testIdPrefix}-date-row`}>
        {dateOptions.map((day) => {
          const active = day.value === selectedDate;
          return (
            <button
              key={day.value}
              type="button"
              onClick={() => setSelectedDate(day.value)}
              className={`min-w-[72px] shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${
                active
                  ? 'border-teal-500/50 bg-teal-500/15 text-teal-100'
                  : 'border-zinc-700 bg-zinc-950 text-zinc-300 hover:border-zinc-500'
              }`}
              data-testid={`${testIdPrefix}-date-${day.value}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{day.weekday}</div>
              <div className="text-xs font-semibold">{day.label}</div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-zinc-400 py-6 justify-center" data-testid={`${testIdPrefix}-slots-loading`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading available slots…
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid={`${testIdPrefix}-slots-error`}>
          {error}
        </div>
      ) : slots.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-700 bg-zinc-950 px-3 py-6 text-center text-xs text-zinc-500" data-testid={`${testIdPrefix}-slots-empty`}>
          No open slots on this day. Choose another date or update availability in Appointments settings.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto pr-1" data-testid={`${testIdPrefix}-slots-grid`}>
          {slots.map((slot) => {
            const key = `${slot.start_time}|${slot.end_time}`;
            const active = selectedKey === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect({ ...slot, timezone })}
                className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                  active
                    ? 'border-teal-400 bg-teal-500/20 text-teal-100'
                    : 'border-zinc-700 bg-zinc-950 text-zinc-200 hover:border-teal-500/40 hover:bg-teal-500/10'
                }`}
                data-testid={`${testIdPrefix}-slot-${slot.label.replace(/[:\s]/g, '-')}`}
              >
                {slot.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CrmSlotPicker;
